import { useEffect, useState, useCallback, useRef } from "react";
import AppLayout from "../layouts/AppLayout.jsx";
import NavBusinessClass from "../components/NavBusinessClass.jsx";
import CourseCard from "../components/CourseCard.jsx";
import API from "../config/api.js";

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [buttonDisabled, setButtonDisabled] = useState(true);

  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("currentPage", page);

      const res = await fetch(`${API.courses}?list`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const data = await res.json();

      if (!Array.isArray(data) || data.length === 0) {
        if (page === 1) {
          setCourses([]);
        }

        setHasMore(false);
        return;
      }

      if (page === 1) {
        setCourses(data);
      } else {
        setCourses((prev) => [...prev, ...data]);
      }
    } catch (err) {
      console.error("Error", err);

      if (page === 1) {
        setCourses([]);
      }

      setHasMore(false);
    } finally {
      setLoading(false);
      setButtonDisabled(true);
      setTimeout(() => setButtonDisabled(false), 2000);
    }
  }, [page]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  // Infinite scroll com IntersectionObserver
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (!hasMore || loading || buttonDisabled) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, buttonDisabled]);

  const handleLoadMore = () => {
    if (!buttonDisabled && !loading && hasMore) {
      setPage((prev) => prev + 1);
    }
  };

  const placeholders = Array.from({ length: 8 }, (_, i) => (
    <div key={`placeholder-${i}`} className="col-md-3">
      <div className="card rounded-4 h-100 border animate__animated animate__fadeIn animate__slow">
        <div className="placeholder-glow">
          <div
            className="placeholder w-100 rounded-top-4"
            style={{ height: "160px" }}
          ></div>
        </div>
        <div className="card-body">
          <h5 className="card-title">
            <span className="placeholder col-8"></span>
          </h5>
          <p className="card-text">
            <span className="placeholder col-6"></span><br />
            <span className="placeholder col-4"></span>
          </p>
        </div>
        <div className="card-footer rounded-bottom-4 bg-transparent p-3">
          <button
            type="button"
            className="btn btn-outline-primary disabled placeholder w-100"
            disabled
          >
            Loading...
          </button>
        </div>
      </div>
    </div>
  ));

  return (
    <AppLayout currentPage="courses">
      <NavBusinessClass currentPage="courses" />

      <div className="row g-3">
        {loading && courses.length === 0
          ? placeholders
          : courses.map((course) => (
              <div key={course.idcourse} className="col-md-3">
                <CourseCard course={course} />
              </div>
            ))}
      </div>

      {hasMore && courses.length > 0 && (
        <div className="text-center mt-4" ref={sentinelRef}>
          <button
            type="button"
            onClick={handleLoadMore}
            className="btn btn-primary"
            disabled={buttonDisabled || loading}
          >
            {loading ? "Loading..." : "Load More Courses"}
          </button>
        </div>
      )}
    </AppLayout>
  );
}